"use client";

import { ld } from "@/lib/theme/light-dark";
import { ArrowRight } from "lucide-react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import type { ReactNode } from "react";
import { useRef } from "react";

import { headlinePart } from "../../../../../features/_components/feature-section-heading";
import { H } from "../../hero-tokens";
import {
  ResolutionModeProvider,
  useReportInView,
  useResolutionMode,
} from "../resolution/resolution-mode";
import { MigrationPlan } from "./migration-plan";
import { MigrationSources, MigrationSourcesCompact, MigrationWires } from "./migration-sources";
import "./migration.css";

// i18n-exempt: route path, not copy
const MIGRATION_SERVICE_PATH = "/migration-service";

/** Plays once each time it scrolls in from below; reduced motion shows the finished run. */
function MigrationStage(): ReactNode {
  const { running, run } = useResolutionMode();
  const ref = useRef<HTMLDivElement | null>(null);
  useReportInView(ref, 0.35);
  return (
    <div ref={ref} className="mkmg-comp">
      <div key={run} className={["mkmg-canvas", running ? "" : "mkmg-paused"].join(" ")}>
        <MigrationSources />
        <MigrationSourcesCompact />
        <MigrationWires />
        <MigrationPlan />
      </div>
    </div>
  );
}

/** Moving service: every old system, done by us in one week; quiet link to the prices page. */
export function MigrationSection(props: {
  /** Phone: smaller headline. */
  readonly compact: boolean;
}): ReactNode {
  const t = useTranslations("marketing.migrationStage");
  return (
    <section
      aria-labelledby="migration-heading"
      style={{
        maxWidth: 1240,
        margin: props.compact ? "88px auto 0" : "150px auto 0",
        padding: `0 ${props.compact ? 20 : 28}px`,
      }}
    >
      <div style={{ textAlign: "center", maxWidth: 900, margin: "0 auto" }}>
        <h2
          id="migration-heading"
          style={{
            margin: 0,
            fontFamily: H.display,
            fontWeight: 400,
            fontSize: props.compact ? 33 : "clamp(2rem, 4vw, 3.25rem)",
            lineHeight: props.compact ? 1.12 : 1.08,
            letterSpacing: "-0.018em",
            textWrap: "balance",
            color: ld("#0B0F19", "#fafafa"),
          }}
        >
          {t.rich("headline", { muted: headlinePart })}
        </h2>
        <p
          style={{
            margin: "16px auto 0",
            maxWidth: 640,
            fontSize: props.compact ? 16 : "clamp(1rem, 1.3vw, 1.125rem)",
            lineHeight: 1.55,
            textWrap: "pretty",
            color: ld("rgba(11,15,25,0.6)", "rgba(250,250,250,0.6)"),
          }}
        >
          {t("sub")}
        </p>
        {/* Prices stay one quiet click away (Jonathan 04.11.2026). */}
        <Link href={MIGRATION_SERVICE_PATH} className="mkmg-plan-link">
          {t("pricesLink")}
          <ArrowRight size={15} strokeWidth={2} aria-hidden="true" />
        </Link>
      </div>
      <div style={{ maxWidth: 1080, margin: props.compact ? "32px auto 0" : "56px auto 0" }}>
        <ResolutionModeProvider>
          <MigrationStage />
        </ResolutionModeProvider>
      </div>
    </section>
  );
}
